import React, { useEffect, useState } from "react";
import Col from "react-bootstrap/esm/Col";
import Row from "react-bootstrap/esm/Row";
import { TableColumn } from "react-data-table-component";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useGetCompanies } from "../../api/Company";
import getIcon, { Icon, IconSize } from "../../assets/Icons/IconClassNames";
import HeaderContainer from "../../components/ContainerComponents/HeaderContainer";
import MainContainer from "../../components/ContainerComponents/MainContainer";
import LoadingSpinner from "../../components/LoadingSpinner";
import When from "../../components/When";
import { ActionPlanType, CompanyRole } from "../../core/enums/enums";
import { displayGreeting } from "../../core/helpers/greetingHelper";
import { ICompany } from "../../core/interfaces/company";
import { IDocument } from "../../core/interfaces/document";
import useAccessCheck from "../../hooks/useAccessCheck";
import { useAppSelector } from "../../store/hooks";
import NewIncidentModal from "./Components/Incident/NewIncidentModal";
import LastDocumentTable from "./Components/LastDocumentTable";
import DashboardUnHandledReadReceipts from "./Components/ReadReceipt/DashboardUnHandledReadReceipts";
import DashboardTodo from "./Components/Todo/DashboardTodo";
import DashboardCard from "./DashboardCard";
import { DashboardCardContentImage } from "./DashboardCardContent";


const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const user = useAppSelector((state) => state.user);

    const { cId } = useParams();

    const { isFetching, data: companies } = useGetCompanies(cId);

    const [company, setCompany] = useState<ICompany | null>(null);
    const [isLoading, setIsLoading] = useState(isFetching);
    const [showReport, setShowReport] = useState(false);
    const [checkAccess] = useAccessCheck();

    useEffect(() => {
        setIsLoading(true);
        if (cId !== user.companyId) return;
        if (!companies || companies.data === undefined || companies.data.data.length === 0) return;

        const selectedCompany = companies.data.data.find((c) => c.id === user.companyId);
        if (selectedCompany) {
            setCompany(selectedCompany);
        }
        setIsLoading(false);
    }, [cId, user.companyId, companies]);


    const dashboardContent = () => (
        <>
            <Col lg={3} className="pb-3">
                <Row>
                    <Col className="mt-3">
                        <DashboardTodo />
                    </Col>
                </Row>
            </Col>

            <Col lg={9} className="pb-3">
                <Row>
                    <Col md={4}>
                        <Row className="mb-3 mt-3">
                            <Col>
                                <When condition={checkAccess(CompanyRole.ADMIN)}
                                    otherwise={<DashboardCard fetching={false} theme={'lightblue'} />}>
                                    <DashboardUnHandledReadReceipts />
                                </When>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <DashboardCard fetching={false} theme={"lightblue"} height="12rem">
                                    {/* <DashboardCardContentStatistics value={numNyaKunder} title={"Nya kunder senast 30 dgr"} /> */}
                                </DashboardCard>
                            </Col>
                        </Row>
                    </Col>

                    <Col md={4}>
                        <Row className="mb-3 mt-3">
                            <Col>
                                {user.parentCompanyId ? (
                                    <DashboardCard fetching={false} theme={"green"} height="12rem" onClick={() => navigate("/c")}>
                                        <DashboardCardContentImage
                                            image={<i className={getIcon(Icon.CUSTOMERS, IconSize.X5)}
                                                title={"Anslutna företag"}></i>}
                                            title={"Anslutna företag"}
                                        />
                                    </DashboardCard>
                                ) : (
                                    <DashboardCard fetching={false} theme={"green"} height="12rem">
                                    </DashboardCard>
                                )}
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <DashboardCard fetching={false} theme={"green"} height="12rem"
                                    onClick={() => navigate("documents#root")}>
                                    <DashboardCardContentImage
                                        image={<i className={getIcon(Icon.CLIPBOARD, IconSize.X5)}
                                            title={"Mina dokument"}></i>}
                                        title={"Mina dokument"}
                                    />
                                </DashboardCard>
                            </Col>
                        </Row>
                    </Col>

                    <Col md={4}>
                        <Row className="mb-3 mt-3">
                            <Col>
                                <DashboardCard fetching={false} theme={"darkblue"} height="12rem"
                                    onClick={() => setShowReport(true)}>
                                    <DashboardCardContentImage
                                        image={<i className={getIcon(Icon.INCIDENT, IconSize.X5)}
                                            title={"Rapportera tillbud, olycka eller avvikelse"}></i>}
                                        title={"Rapportera tillbud, olycka eller avvikelse"}
                                    />
                                </DashboardCard>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <DashboardCard fetching={false} theme={"darkblue"} height="12rem"
                                    onClick={() => navigate("action-plan")}>
                                    <DashboardCardContentImage
                                        image={<i className={getIcon(Icon.CALENDAR, IconSize.X5)}
                                            title={"Handlingsplan"}></i>}
                                        title={"Handlingsplan"}
                                    />
                                </DashboardCard>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Col>
        </>
    );

    const columns: TableColumn<IDocument>[] = [
        {
            name: 'Dokument',
            cell: cell => <Link to={"documents/" + cell.id}>{cell.name}</Link>,
            sortable: true,
        }
    ];

    const Table = () => (
        <>
            <Col>
                <LastDocumentTable />
            </Col>
        </>
    );

    const renderView = () => (
        <MainContainer>
            <HeaderContainer title={displayGreeting(user.firstName)} />
            <Row className="mb-4">
                {dashboardContent()}
            </Row>
            <HeaderContainer title={"Mina senaste dokument"} />
            <Row>
                {Table()}
            </Row>
            <NewIncidentModal
                initialActionPlanType={ActionPlanType.INCIDENT}
                show={showReport}
                setShow={() => setShowReport(false)}
            />
        </MainContainer>
    )

    const renderDashboard = () => {
        if (isLoading) return <MainContainer><LoadingSpinner /></MainContainer>;
        if (!company) return <MainContainer>Hittar ej företag!</MainContainer>;
        return renderView();
    }

    return renderDashboard();
}

export default Dashboard;